'use es6';

import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import { jsx as _jsx } from "react/jsx-runtime";
import quickFetch from 'quick-fetch';
import { Fragment } from 'react';
import ReactDOM from 'react-dom'; //I18n Configuration

import createPublicI18nProvider from 'I18n/init/providers/createPublicI18nProvider';
import { initAppTranslations } from 'I18n/init/setup/AppInit';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import timezoneLang from 'i2l?query=sporks!timezone-utils/lang/en.lyaml';
import 'I18n/init/moment/standardMomentLocales';
import 'I18n/init/moment/publicMomentLocales';
import 'I18n/init/data/allTimezones';
import connectI18nLoader from './connectI18nLoader';
import { RhumbProvider, NavMarker } from 'react-rhumb';
import staticAdapter from 'react-rhumb/staticAdapter';
import rhumbConfig from '../rhumb-config.yaml';
import Url from 'urlinator/Url';
import noAuthApiClient from 'hub-http/clients/noAuthApiClient';
import { getSlug } from 'MeetingsBase/utils/url';
import { mapFalsyParamsToNull, constructFormState } from 'MeetingsPublic/utils/urlParams';
import createBookingComponent from 'MeetingsLib/components/createBookingComponent';
import pick from 'transmute/pick';
import omit from 'transmute/omit';
import keySeq from 'transmute/keySeq';
import { ravenDataCallback } from 'MeetingsLib/utils/errorUtils';
import Raven from 'Raven';

var DatePickerMarker = function DatePickerMarker() {
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "DATE_PICKER_BUTTON_LOAD"
  });
};

var TimePickerMarker = function TimePickerMarker() {
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "TIME_PICKER_LOAD"
  });
};

var NotFoundMarker = function NotFoundMarker() {
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "LINK_NOT_FOUND"
  });
};

var ErrorMarker = function ErrorMarker() {
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "BOOKING_LOAD_FAILURE"
  });
};

var EmailFormMarker = function EmailFormMarker() {
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "EMAIL_FORM_LOAD"
  });
};

var Application = /*#__PURE__*/function () {
  function Application() {
    _classCallCheck(this, Application);
  }

  _createClass(Application, [{
    key: "init",
    value: function init() {
      var _this = this;

      if (window.newrelic && window.performance && window.performance.getEntriesByName) {
        var scriptStartEntries = window.performance.getEntriesByName('scriptStartTime');

        if (scriptStartEntries && scriptStartEntries.length > 0) {
          window.newrelic.setCustomAttribute('scriptStartTime', scriptStartEntries[0].startTime);
          window.newrelic.setCustomAttribute('appStartTime', window.performance.now());
        }
      }

      Raven.setDataCallback(ravenDataCallback);
      var I18nProvider = createPublicI18nProvider();
      var langAvailable = initAppTranslations([I18nProvider.register(lang), I18nProvider.register(timezoneLang)]);
      langAvailable.then(function () {
        return _this.launch();
      });
    }
  }, {
    key: "launch",
    value: function launch() {
      var url = new Url(window.location);
      var slug = getSlug(window.location.href);
      var embedded = url.paramValue('embed') === 'true';
      var hasRescheduleParam = url.paramValue('reschedule') === 'true';
      var meetingChangeId = url.paramValue('meetingChangeId');
      var isRescheduling = !embedded && hasRescheduleParam && !!meetingChangeId;
      var forceOffline = url.paramValue('forceOffline') === 'true';
      var troubleshooting = url.paramValue('troubleshoot') === 'true';
      var urlParams = mapFalsyParamsToNull(url);
      var trackingParams = pick(['utm_campaign', 'utm_source', 'utm_medium', 'meeting_funnel', 'meeting_product', 'meeting_medium', 'meeting_type', 'meeting_source', 'meeting_campaign', 'source_app', 'hubs_signup-url', 'hubs_signup-cta', 'hubs_content', 'hubs_content-cta', 'hubs_offer', 'hubs_post', 'hubs_post-cta'], urlParams);
      var forcePropertyForm;

      try {
        forcePropertyForm = JSON.parse(url.paramValue('forcePropertyForm'));
      } catch (e) {
        forcePropertyForm = null;
      }

      var parentPageHSAnalyticsInfo = pick(['parentPageUrl', 'parentHubspotUtk'], urlParams);
      var formFieldUrlParams = omit(keySeq(parentPageHSAnalyticsInfo), omit(keySeq(trackingParams), urlParams));
      var formState = constructFormState(formFieldUrlParams);

      if (window.newrelic) {
        var requestState = quickFetch.getRequestStateByName('book-info');
        var bookInfoFetchPreRender = !!(requestState && requestState.finished && !requestState.errored);
        window.newrelic.setCustomAttribute('bookInfoFetchPreRender', bookInfoFetchPreRender);
      }

      if (window.performance && window.performance.mark) {
        window.performance.mark('create-booking-component');
      }

      var BookingComponent = createBookingComponent({
        httpClient: noAuthApiClient,
        slug: slug,
        embedded: embedded,
        forceOffline: forceOffline,
        troubleshooting: troubleshooting,
        forcePropertyForm: forcePropertyForm,
        trackingParams: trackingParams,
        formState: formState,
        isRescheduling: isRescheduling,
        meetingChangeId: meetingChangeId,
        parentPageHSAnalyticsInfo: parentPageHSAnalyticsInfo,
        allowTroubleshootMode: true,
        allowRedirect: true
      });
      var WrappedComponent = connectI18nLoader(BookingComponent);
      var appRoute = document.getElementsByClassName('page')[0];
      window.disabledHsPopups = ['LEADFLOW', 'FEEDBACK'];

      if (embedded) {
        appRoute.className += ' embedded'; // set disabled popups so that scripts don't load twice
        // (once on parent page and once in iframe) on embedded meetings

        window.disabledHsPopups.push('PRIVACY', 'ADS', 'LIVE_CHAT'); // set __hsUserToken as utk passed in from parent page so
        // analytics tracker will init with consistent utk across iframe and parent
        // otherwise, we have form submission and view with incorrect source

        window.__hsUserToken = urlParams.parentHubspotUtk;
      }

      ReactDOM.render( /*#__PURE__*/_jsx(Fragment, {
        children: /*#__PURE__*/_jsx(RhumbProvider, {
          captureExceptions: true,
          config: rhumbConfig,
          history: staticAdapter('/'),
          children: /*#__PURE__*/_jsx(WrappedComponent, {
            hideLogo: embedded,
            DatePickerMarker: DatePickerMarker,
            TimePickerMarker: TimePickerMarker,
            NotFoundMarker: NotFoundMarker,
            ErrorMarker: ErrorMarker,
            EmailFormMarker: EmailFormMarker
          })
        })
      }), appRoute);
    }
  }]);

  return Application;
}();

export { Application as default };